import React, { useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { InfoCard, Progress } from '@backstage/core-components';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  dangerButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': { backgroundColor: theme.palette.error.dark },
  },
  warningText: { color: theme.palette.error.main, fontWeight: 600 },
}));

type DestroyStatus = 'idle' | 'queuing' | 'waiting' | 'removing' | 'done' | 'error';

interface QueuedRun {
  runId: number;
  runUrl: string;
  organization: string;
  project: string;
}

/**
 * Management card for AzureEnvironmentProvider entities.
 *
 * Flow:
 *  1. Queue the terraform destroy pipeline run
 *  2. Poll every 10 s until the run reaches `completed`
 *  3. If succeeded → remove catalog entity and redirect
 *     If failed/cancelled → surface the error, leave entity in catalog
 */
export function AzureDeleteProviderCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const entityRef = `azureenvironmentprovider:${entity.metadata.namespace ?? 'default'}/${entity.metadata.name}`;
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const navigate = useNavigate();

  const meta = entity.metadata as Record<string, any>;
  const providerName = entity.metadata.name;
  const organization = meta.adoOrganization as string | undefined;
  const project = meta.adoProject as string | undefined;
  const pipelineName = meta.adoPipelineName as string | undefined;
  const repoName = meta.adoRepoName as string | undefined;
  const canDestroy = !!(organization && project && pipelineName);

  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [status, setStatus] = useState<DestroyStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [queuedRun, setQueuedRun] = useState<QueuedRun | null>(null);
  const [pollStatus, setPollStatus] = useState('');

  const statusLabel: Record<DestroyStatus, string> = {
    idle: '',
    queuing: 'Queuing destroy pipeline…',
    waiting: 'Waiting for destroy pipeline to complete…',
    removing: 'Removing catalog entry…',
    done: 'Done! Redirecting to catalog…',
    error: '',
  };

  const handleDelete = async () => {
    setStatus('queuing');
    setErrorMsg('');
    setPollStatus('');

    try {
      const baseUrl = await discoveryApi.getBaseUrl('azure-apps');
      const { token } = await identityApi.getCredentials();

      // 1. Queue the destroy pipeline
      if (canDestroy) {
        const destroyRes = await fetch(`${baseUrl}/entities/destroy`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ entityRef, organization, project, pipelineName }),
        });
        if (!destroyRes.ok) {
          const body = await destroyRes.json().catch(() => ({ error: destroyRes.statusText }));
          throw new Error(body.error ?? destroyRes.statusText);
        }
        const data = await destroyRes.json();
        const run: QueuedRun = {
          runId: data.runId,
          runUrl: data.runUrl,
          organization: organization!,
          project: project!,
        };
        setQueuedRun(run);

        // 2. Poll until completed
        setStatus('waiting');
        const POLL_INTERVAL_MS = 10_000;
        for (;;) {
          await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
          try {
            const statusRes = await fetch(
              `${baseUrl}/pipelines/run-status?organization=${encodeURIComponent(run.organization)}&project=${encodeURIComponent(run.project)}&runId=${run.runId}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (!statusRes.ok) continue;
            const statusData = await statusRes.json();
            setPollStatus(`Pipeline: ${statusData.status}${statusData.result ? ` (${statusData.result})` : ''}`);

            if (statusData.status === 'completed') {
              if (statusData.result !== 'succeeded') {
                throw new Error(
                  `Destroy pipeline finished with result "${statusData.result}". The provider has NOT been removed from the catalog. Check the pipeline run for details.`,
                );
              }
              break;
            }
          } catch (pollErr: any) {
            // Only re-throw errors we deliberately constructed above
            if (pollErr.message?.includes('Destroy pipeline finished')) throw pollErr;
            // Transient fetch error — keep polling
          }
        }
      }

      // 3. Pipeline succeeded (or no pipeline) — remove from catalog
      setStatus('removing');
      const locationAnnotation =
        entity.metadata.annotations?.['backstage.io/managed-by-location'];
      if (locationAnnotation) {
        try {
          const location = await catalogApi.getLocationByRef(locationAnnotation);
          if (location) await catalogApi.removeLocationById(location.id);
        } catch {
          // best-effort
        }
      }
      await catalogApi.removeEntityByUid(entity.metadata.uid!);

      // Best-effort: delete the ADO repo (also removes all pipeline definitions for it).
      // Provider has its own dedicated repo; if the repo deletion fails the catalog entry
      // is already gone so we still redirect.
      if (organization && project && repoName) {
        try {
          await fetch(
            `${baseUrl}/ado/repo?entityRef=${encodeURIComponent(entityRef)}&organization=${encodeURIComponent(organization)}&project=${encodeURIComponent(project)}&repoName=${encodeURIComponent(repoName)}`,
            { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
          );
        } catch {
          // best-effort — user can clean up ADO manually
        }
      } else if (organization && project && pipelineName) {
        // Fallback: delete the named pipeline definition if no repo metadata is available
        try {
          await fetch(
            `${baseUrl}/ado/pipeline?entityRef=${encodeURIComponent(entityRef)}&organization=${encodeURIComponent(organization)}&project=${encodeURIComponent(project)}&pipelineName=${encodeURIComponent(pipelineName)}`,
            { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
          );
        } catch {
          // best-effort
        }
      }

      setStatus('done');
      setTimeout(() => navigate('/catalog'), 3000);
    } catch (e: any) {
      setStatus('error');
      setErrorMsg(e.message ?? 'Unknown error');
    }
  };

  const isBusy = status === 'queuing' || status === 'waiting' || status === 'removing';

  return (
    <>
      <InfoCard title="Management" noPadding={false}>
        <Typography variant="body2" gutterBottom>
          Permanently destroy the infrastructure backing this environment provider and remove it
          from the catalog.
        </Typography>
        {canDestroy ? (
          <Typography variant="body2" gutterBottom>
            Pipeline: <strong>{pipelineName}</strong>
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary" gutterBottom>
            No pipeline metadata found — only the catalog entry will be removed.
          </Typography>
        )}
        <div style={{ marginTop: 16 }}>
          <Button
            variant="contained"
            className={classes.dangerButton}
            startIcon={<DeleteForeverIcon />}
            onClick={() => setOpen(true)}
            disabled={isBusy || status === 'done'}
          >
            Delete Provider
          </Button>
        </div>
      </InfoCard>

      <Dialog
        open={open}
        onClose={() => { if (!isBusy) setOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Provider: {providerName}</DialogTitle>
        <DialogContent>
          {status === 'idle' && (
            <>
              <DialogContentText>This will:</DialogContentText>
              <ul>
                {canDestroy && (
                  <li>Queue a <strong>terraform destroy</strong> pipeline run to delete all Azure infrastructure</li>
                )}
                <li>Wait for the destroy pipeline to complete successfully</li>
                <li>Remove <strong>{providerName}</strong> from the Backstage catalog</li>
              </ul>
              <DialogContentText className={classes.warningText}>
                This action is irreversible. Type <strong>{providerName}</strong> to confirm.
              </DialogContentText>
              <TextField
                autoFocus
                fullWidth
                margin="dense"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder={providerName}
                variant="outlined"
              />
            </>
          )}

          {isBusy && (
            <>
              <Progress />
              <DialogContentText style={{ marginTop: 16 }}>
                {statusLabel[status]}
              </DialogContentText>
              {queuedRun && (
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary={pipelineName}
                      secondary={
                        <a href={queuedRun.runUrl} target="_blank" rel="noopener noreferrer">
                          View pipeline run →
                        </a>
                      }
                    />
                  </ListItem>
                </List>
              )}
              {pollStatus && (
                <DialogContentText variant="caption" color="textSecondary">
                  {pollStatus}
                </DialogContentText>
              )}
            </>
          )}

          {status === 'done' && (
            <DialogContentText>
              Destroy pipeline completed successfully. Catalog entry removed. Redirecting…
            </DialogContentText>
          )}

          {status === 'error' && (
            <DialogContentText color="error">{errorMsg}</DialogContentText>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => { setOpen(false); setConfirmText(''); setStatus('idle'); setErrorMsg(''); }}
            disabled={isBusy}
          >
            {status === 'error' ? 'Close' : 'Cancel'}
          </Button>
          {status === 'idle' && (
            <Button
              className={classes.dangerButton}
              variant="contained"
              onClick={handleDelete}
              disabled={confirmText !== providerName}
            >
              Delete
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
